<script setup lang="ts">
import { ChevronDown, ChevronUp, DatabaseZap, Trash2 } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/editing/ConfirmDialog.vue';

const props = withDefaults(defineProps<{
    workspaceName?: string;
    recordCount?: number;
    canRemove?: boolean;
    removing?: boolean;
    title?: string;
    description?: string;
    removeLabel?: string;
    confirmTitle?: string;
    confirmDescription?: string;
}>(), {
    workspaceName: 'this workspace',
    recordCount: 0,
    canRemove: false,
    removing: false,
    title: 'You are exploring with demo data',
    description: 'Every sample record is clearly labelled and can be removed together when you are ready to add real work.',
    removeLabel: 'Remove demo data',
    confirmTitle: 'Remove all demo data?',
    confirmDescription: 'This permanently removes only the records created by the Vuey starter dataset. Anything your team created will be kept.',
});

const emit = defineEmits<{
    remove: [];
}>();

const confirmOpen = ref(false);
const mobileCollapsed = ref(false);
const explanationStorageKey = computed(() => `vuey:demo-banner-explained:${props.workspaceName}`);

onMounted(() => {
    try {
        mobileCollapsed.value = window.localStorage.getItem(explanationStorageKey.value) === '1';
    } catch {
        mobileCollapsed.value = false;
    }
});

function toggleMobileDetails() {
    mobileCollapsed.value = !mobileCollapsed.value;
    try {
        window.localStorage.setItem(explanationStorageKey.value, mobileCollapsed.value ? '1' : '0');
    } catch {
        // Local persistence is optional.
    }
}

function confirmRemoval() {
    emit('remove');
}
</script>

<template>
    <section class="mx-4 mb-4 border-y border-primary/15 bg-primary/5 px-4 py-3 md:mx-6" aria-label="Demo data status">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex min-w-0 items-start gap-3">
                <span class="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <DatabaseZap class="size-4" />
                </span>
                <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                        <p class="text-sm font-semibold text-foreground">{{ title }}</p>
                        <Badge variant="secondary">Demo data</Badge>
                        <Badge v-if="recordCount > 0" variant="outline">{{ recordCount }} records</Badge>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            class="ml-auto size-8 sm:hidden"
                            :aria-label="mobileCollapsed ? 'Show demo data details' : 'Hide demo data details'"
                            @click="toggleMobileDetails"
                        >
                            <ChevronDown v-if="mobileCollapsed" class="size-4" />
                            <ChevronUp v-else class="size-4" />
                        </Button>
                    </div>
                    <p class="mt-1 max-w-3xl text-sm leading-5 text-muted-foreground" :class="mobileCollapsed ? 'hidden sm:block' : 'block'">
                        {{ description }}
                    </p>
                </div>
            </div>

            <Button
                v-if="canRemove"
                type="button"
                variant="outline"
                size="sm"
                class="shrink-0"
                :disabled="removing"
                @click="confirmOpen = true"
            >
                <Trash2 class="mr-2 size-4" />
                {{ removing ? 'Removing…' : removeLabel }}
            </Button>
        </div>

        <ConfirmDialog
            v-model:open="confirmOpen"
            :title="confirmTitle"
            :description="`${confirmDescription} Workspace: ${workspaceName}.`"
            confirm-label="Remove demo data"
            destructive
            :loading="removing"
            @confirm="confirmRemoval"
        />
    </section>
</template>
